import 'normalize.css';
import { extendTheme } from '@chakra-ui/react';
import { css } from '@emotion/react';
import { Capacitor } from '@capacitor/core';
import { Rating } from '../apis/backend';

import HelveticaNowDisplayRegular from './fonts/HelveticaNowDisplay-Regular.woff2';
import HelveticaNowDisplayBold from './fonts/HelveticaNowDisplay-Bold.otf';
import HelveticaNowDisplayXBold from './fonts/HelveticaNowDisplay-XBd.otf';
import HelveticaNowTextLight from './fonts/HelveticaNowText-Light.woff2';
import HelveticaNowTextMedium from './fonts/HelveticaNowText-Medium.woff2';
import HelveticaNowTextMediumItalics from './fonts/HelveticaNowText-MediumIt.otf';
import HelveticaNowTextRegular from './fonts/HelveticaNowText-Regular.woff2';
import HelveticaNowMicroMedium from './fonts/HelveticaNowMicro-Medium.woff2';

export const colors = {
  cloud: '#CBCAC9',
  orange: '#D03F17',
  petrolBlue: '#118383',
  liquorice: '#171616',
  obsidian: '#222222',
  silver: '#74726D',
  sugar: '#fffefa',
  snow: '#FFFFFF',
  fog: '#F8F8F8',
  tungsten: '#504E4B',
  berry: '#D03F17',
  berryAir: '#FAEBE5',
  earth: '#836714',
  earthAir: '#F2EFE7',
  leaf: '#189D39',
  leafAir: '#E7F5EB',
  skyBlue: '#28B5BD',
  skyAir: '#E5F5F7',
  modes: {
    light: {
      background: '#fffefa', // colors.sugar
      text: '#171616', // colors.liquorice
      border: '#CBCAC9', // colors.cloud
      link: '#118383', // colors.petrolBlue
      statusBar: '#fffefa', // colors.sugar
    },
    dark: {
      background: '#171616', // colors.liquorice
      text: '#fffefa', // colors.sugar
      border: '#504E4B', // colors.tungsten
      link: '#28B5BD', // colors.skyBlue
      statusBar: '#171616',
    },
  },
};

export const boxShadow = 'box-shadow: 0 1px 3px rgba(23, 22, 22, 0.2);';

export const borderRadius = '8px';

export const spacing = {
  micro: '2px',
  milli: '4px',
  centi: '8px',
  deci: '16px',
  regular: '24px',
  deka: '32px',
  hecto: '40px',
  kilo: '48px',
  giga: '64px',
};

export const fontFamilies = {
  helveticaNowText: `'HelveticaNowText', sans-serif`,
  helveticaNowDisplay: `'HelveticaNowDisplay', sans-serif`,
  helveticaNowDisplayBold: `'HelveticaNowDisplay-Bold', sans-serif`,
  helveticaNowDisplayXBold: `'HelveticaNowDisplay-XBd', sans-serif`,
  helveticaNowMicro: `'HelveticaNowMicro', sans-serif`,
};

export const fontStyles = {
  elephant: css`
    font-family: ${fontFamilies.helveticaNowDisplayXBold};
    font-size: 2.25rem;
    line-height: 2.2rem;
  `,
  rhino: css`
    font-family: ${fontFamilies.helveticaNowDisplay};
    font-weight: 400;
    font-size: 2.25rem;
    line-height: 1.2rem;
  `,
  shark: css`
    font-family: ${fontFamilies.helveticaNowText};
    font-weight: 500;
    font-size: 1.38rem;
    line-height: 1.88rem;
  `,
  roboShark: css`
    font-family: ${fontFamilies.helveticaNowMicro};
    font-weight: 500;
    font-size: 1.25rem;
    line-height: 1.88rem;
  `,
  horse: css`
    font-family: ${fontFamilies.helveticaNowText};
    font-weight: 500;
    font-size: 1.16rem;
    line-height: 1.5rem;
  `,
  roboHorse: css`
    font-family: ${fontFamilies.helveticaNowMicro};
    font-weight: 500;
    font-size: 1rem;
    line-height: 1.5rem;
  `,
  lion: css`
    font-family: ${fontFamilies.helveticaNowText};
    font-weight: 500;
    font-size: 1.06rem;
    line-height: 1.44rem;
  `,
  roboLion: css`
    font-family: ${fontFamilies.helveticaNowMicro};
    font-weight: 500;
    font-size: 0.92rem;
    line-height: 1.44rem;
  `,
  dog: css`
    font-family: ${fontFamilies.helveticaNowText};
    font-weight: 400;
    font-size: 1rem;
    line-height: 1.5rem;
  `,
  dogItalics: css`
    font-family: ${fontFamilies.helveticaNowText};
    font-weight: 400;
    font-size: 1rem;
    line-height: 1.5rem;
    font-style: italic;
  `,
  cat: css`
    font-family: ${fontFamilies.helveticaNowText};
    font-weight: 400;
    font-size: 0.88rem;
    line-height: 1.31rem;
  `,
  catItalics: css`
    font-family: ${fontFamilies.helveticaNowText};
    font-weight: 400;
    font-size: 0.88rem;
    line-height: 1.31rem;
    font-style: italic;
  `,
  fatCat: css`
    font-family: ${fontFamilies.helveticaNowText};
    font-weight: 500;
    font-size: 0.88rem;
    line-height: 1.31rem;
  `,
  roboCat: css`
    font-family: ${fontFamilies.helveticaNowMicro};
    font-weight: 400;
    font-size: 0.81rem;
    line-height: 1.31rem;
  `,
  mouse: css`
    font-family: ${fontFamilies.helveticaNowText};
    font-weight: 500;
    font-size: 0.81rem;
    line-height: 1.19rem;
  `,
  thinMouse: css`
    font-family: ${fontFamilies.helveticaNowText};
    font-weight: 400;
    font-size: 0.81rem;
    line-height: 1.19rem;
  `,
  roboMouse: css`
    font-family: ${fontFamilies.helveticaNowMicro};
    font-weight: 400;
    font-size: 0.75rem;
    line-height: 1.19rem;
  `,
  flea: css`
    font-family: ${fontFamilies.helveticaNowText};
    font-weight: 500;
    font-size: 0.75rem;
    line-height: 1.06rem;
  `,
  thinFlea: css`
    font-family: ${fontFamilies.helveticaNowText};
    font-weight: 400;
    font-size: 0.75rem;
    line-height: 1.19rem;
  `,
  fairyfly: css`
    font-family: ${fontFamilies.helveticaNowText};
    font-weight: 500;
    font-size: 0.6875rem;
    line-height: 1.06rem;
  `,
  roboMite: css`
    font-family: ${fontFamilies.helveticaNowMicro};
    font-weight: 400;
    font-size: 0.5625rem;
    line-height: 1.06rem;
  `,
};

export const darkmodeStyles = {
  regular: css`
    html[data-theme='dark'] & {
      background-color: ${colors.liquorice};
      color: ${colors.sugar};
    }
  `,
};

export const mediaQuery = {
  small: 'min-width: 576px',
  medium: 'min-width: 768px',
};

export const statusColorMap: { [index: string]: string } = {
  NONE: colors.liquorice,
  POSITIVE: colors.leaf,
  NEGATIVE: colors.berry,
  NEUTRAL: colors.tungsten,
};

export const ratingTextColor: { [index in Rating]: string } = {
  [Rating.Exceptional]: colors.leaf,
  [Rating.Favorable]: colors.leaf,
  [Rating.Average]: colors.silver,
  [Rating.Unfavorable]: colors.orange,
  [Rating.Dangerous]: colors.berry,
  [Rating.Missing]: colors.tungsten,
};

export const calculateChangeColor = (changeRatio = 0): string => {
  if (changeRatio < 0.001 && changeRatio > -0.001) return colors.silver;

  const positiveLowerBoundRgb = [163, 200, 17];
  const positiveUpperBoundRgb = [24, 157, 75];
  const negativeLowerBoundRgb = [212, 162, 31];
  const negativeUpperBoundRgb = [218, 63, 23];

  const lowerBoundRgb = changeRatio > 0 ? positiveLowerBoundRgb : negativeLowerBoundRgb;
  const upperBoundRgb = changeRatio > 0 ? positiveUpperBoundRgb : negativeUpperBoundRgb;

  const maxIntensityChangeRatio = 0.1;

  const calculateGradient = (lowerBoundColorCode: number, upperBoundColorCode: number) => {
    const colorRatio = Math.min(Math.abs(changeRatio) / maxIntensityChangeRatio, 1);
    return Math.round(lowerBoundColorCode + (upperBoundColorCode - lowerBoundColorCode) * colorRatio);
  };

  return `rgb(${calculateGradient(lowerBoundRgb[0], upperBoundRgb[0])},${calculateGradient(
    lowerBoundRgb[1],
    upperBoundRgb[1],
  )},${calculateGradient(lowerBoundRgb[2], upperBoundRgb[2])})`;
};

/**
 * Builds page spacing with safe area insets automatically added.
 *
 * CRITICAL: This function is required because StatusBar.overlaysWebView is set to `true`
 * in capacitor.config.json. This means the status bar overlays the webview content,
 * allowing content to appear underneath it. Without adding safe area insets, content
 * would be hidden behind:
 * - Status bar (top)
 * - iPhone notch or Dynamic Island (top)
 * - Home indicator / navigation bar (bottom)
 * - Screen edges with rounded corners (left/right)
 *
 * The function uses CSS env() with built-in fallback values. Browsers without env() support
 * will ignore the calc() expression, allowing CSS cascade to fall back to base padding.
 *
 * @param base - Base spacing value (e.g., spacing.regular, '24px')
 * @param side - Which side to add safe area insets to
 * @returns CSS calc expression combining base spacing + safe area inset with 0px fallback
 *
 * @example
 * padding-top: ${buildPageSpacing(spacing.regular, 'TOP')}
 * // Result: calc(24px + env(safe-area-inset-top, 0px))
 */
export const buildPageSpacing = (base: string, side: 'TOP' | 'BOTTOM' | 'LEFT' | 'RIGHT') => {
  // Use max() to pick the larger of the CSS env() safe area inset and the Android
  // fallback CSS variable. Android WebView doesn't always report env(safe-area-inset-*)
  // correctly in edge-to-edge mode, so MainActivity.java injects --android-status-bar-height
  // and --android-nav-bar-height as fallbacks.
  const safeAreaMappings: { [index: string]: string } = {
    TOP: 'max(env(safe-area-inset-top, 0px), var(--android-status-bar-height, 0px))',
    RIGHT: 'env(safe-area-inset-right, 0px)',
    BOTTOM: 'max(env(safe-area-inset-bottom, 0px), var(--android-nav-bar-height, 0px))',
    LEFT: 'env(safe-area-inset-left, 0px)',
  };

  return `calc(${base} + ${safeAreaMappings[side]})`;
};

/**
 * Pre-built safe area inset expressions with Android WebView fallbacks.
 * Use these when you need the raw inset value (not combined with base spacing).
 * For combined spacing (base + inset), use buildPageSpacing() instead.
 */
export const safeAreaInset = {
  top: 'max(env(safe-area-inset-top, 0px), var(--android-status-bar-height, 0px))',
  bottom: 'max(env(safe-area-inset-bottom, 0px), var(--android-nav-bar-height, 0px))',
  left: 'env(safe-area-inset-left, 0px)',
  right: 'env(safe-area-inset-right, 0px)',
};

export const theme = extendTheme({
  styles: {
    global: {
      body: {
        bg: 'var(--background-color)',
        color: 'var(--text-color)',
      },
      // Fix for first touch event not registering on mobile WebView
      '[data-clickable="true"], [role="button"]': {
        cursor: 'pointer',
        touchAction: 'manipulation',
      },
    },
  },
  colors: {
    black: colors.tungsten,
    white: colors.sugar,
  },
});

export const getGlobalStyles = () => css`
  :root {
    color-scheme: light dark;
    --font-size-scale: 1;
    --background-color: ${colors.modes.light.background};
    --text-color: ${colors.modes.light.text};
    --border-color: ${colors.modes.light.border};
    --link-color: ${colors.modes.light.link};
    --card-background: ${colors.fog};
    --input-background: ${colors.sugar};
    --shadow-color: rgba(23, 22, 22, 0.2);
    --secondary-text: ${colors.silver};
    --popup-background: ${colors.snow};
    --dialog-background: ${colors.snow};
    --modal-background: ${colors.snow};
    --overlay-background: rgba(255, 255, 255, 0.5);
  }

  html[data-theme='dark'] {
    --background-color: ${colors.modes.dark.background};
    --text-color: ${colors.modes.dark.text};
    --border-color: ${colors.modes.dark.border};
    --link-color: ${colors.modes.dark.link};
    --card-background: #242424;
    --input-background: #242424;
    --shadow-color: rgba(0, 0, 0, 0.3);
    --secondary-text: ${colors.tungsten};
    --popup-background: #242424;
    --dialog-background: #242424;
    --modal-background: #242424;
    --overlay-background: rgba(0, 0, 0, 0.5);
  }

  body,
  #root,
  #react-entry {
    background-color: var(--background-color);
    color: var(--text-color);
    min-height: 100vh;
    margin: 0;
    padding: 0;
    ${Capacitor.getPlatform() === 'ios' ? '-webkit-user-select: none; user-select: none;' : ''}
    transition:
      background-color 0.3s ease,
      color 0.3s ease;
  }

  .chakra-popover__content,
  .chakra-popover__popper,
  .chakra-modal__content,
  .chakra-modal__dialog,
  .chakra-menu__menu-list,
  .chakra-tooltip,
  [role='tooltip'],
  [role='dialog'],
  .popup,
  .popover,
  .modal,
  .dialog {
    background-color: var(--popup-background) !important;
    color: var(--text-color) !important;
    border-color: var(--border-color) !important;
  }

  .chakra-modal__overlay,
  .chakra-popover__overlay,
  .overlay {
    background-color: var(--overlay-background) !important;
  }
  .chakra-popover__content input,
  .chakra-modal__content input,
  .popup input,
  .popover input,
  .modal input,
  .dialog input {
    background-color: var(--input-background);
    color: var(--text-color);
    border-color: var(--border-color);
  }

  .chakra-popover__content,
  .chakra-popover__popper {
    background-color: var(--popup-background) !important;
    border: 1px solid var(--border-color) !important;
    border-radius: 8px !important;
  }

  .chakra-popover__arrow {
    background: var(--popup-background) !important;
    border-color: var(--border-color) !important;
  }

  [data-popper-arrow]::after {
    display: none !important;
  }

  .chakra-popover__content::before,
  .chakra-popover__content::after,
  .chakra-popover__popper::before,
  .chakra-popover__popper::after {
    box-shadow: none !important;
    content: none !important;
  }

  .card,
  .container,
  .modal,
  .popup,
  [class*='Card'],
  [class*='Modal'],
  [class*='Popup'] {
    background-color: var(--card-background);
    color: var(--text-color);
    border-color: var(--border-color);
  }

  input,
  textarea,
  select,
  .input,
  .textarea,
  .select,
  [class*='Input'],
  [class*='Textarea'],
  [class*='Select'] {
    background-color: var(--input-background);
    color: var(--text-color);
    border: 1px solid var(--border-color);

    &::placeholder {
      color: var(--secondary-text);
    }
  }

  a,
  .link,
  [class*='Link'] {
    color: var(--link-color);
  }

  svg,
  .icon,
  [class*='Icon'] {
    fill: var(--text-color);
    color: var(--text-color);
  }

  hr,
  .divider,
  [class*='Divider'] {
    border-color: var(--border-color);
  }

  .shadow,
  [class*='Shadow'],
  [class*='Card'],
  [class*='Modal'] {
    box-shadow: 0 2px 8px var(--shadow-color);
  }
  table,
  th,
  td,
  .table,
  [class*='Table'] {
    border-color: var(--border-color);
    color: var(--text-color);
  }

  ul,
  ol,
  .list,
  [class*='List'] {
    color: var(--text-color);
  }

  .tooltip,
  .popover,
  [class*='Tooltip'],
  [class*='Popover'] {
    background-color: var(--card-background);
    color: var(--text-color);
    border-color: var(--border-color);
  }

  ::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  ::-webkit-scrollbar-track {
    background: var(--background-color);
  }

  ::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 4px;
  }

  ::selection {
    background-color: var(--link-color);
    color: var(--text-color);
  }

  .disabled,
  [disabled],
  [aria-disabled='true'] {
    opacity: 0.6;
  }

  a {
    color: var(--link-color);
    text-decoration: none;
  }

  input[type='text'],
  input[type='search'],
  input[type='email'],
  textarea {
    background-color: var(--background-color);
    color: var(--text-color);
  }

  input[type='text'],
  input[type='search'],
  input[type='email'],
  input[type='url'],
  input[type='tel'],
  input[type='number'],
  input:not([type]),
  textarea,
  [contenteditable] {
    ${Capacitor.getPlatform() === 'ios' ? '-webkit-user-select: text; user-select: text;' : ''}
  }
  @font-face {
    font-family: 'HelveticaNowDisplay';
    src: url('${HelveticaNowDisplayRegular}') format('woff2');
    font-weight: 400;
  }

  @font-face {
    font-family: 'HelveticaNowDisplay-Bold';
    src: url('${HelveticaNowDisplayBold}') format('opentype');
  }

  @font-face {
    font-family: 'HelveticaNowDisplay-XBd';
    src: url('${HelveticaNowDisplayXBold}') format('opentype');
  }

  @font-face {
    font-family: 'HelveticaNowText';
    src: url('${HelveticaNowTextLight}') format('woff2');
    font-weight: 300;
  }

  @font-face {
    font-family: 'HelveticaNowText';
    src: url('${HelveticaNowTextRegular}') format('woff2');
    font-weight: 400;
  }

  @font-face {
    font-family: 'HelveticaNowText';
    src: url('${HelveticaNowTextMedium}') format('woff2');
    font-weight: 500;
  }

  @font-face {
    font-family: 'HelveticaNowText';
    src: url('${HelveticaNowTextMediumItalics}') format('woff2');
    font-weight: 500;
    font-style: italic;
  }

  @font-face {
    font-family: 'HelveticaNowMicro';
    src: url('${HelveticaNowMicroMedium}') format('woff2');
    font-weight: 400;
  }

  html {
    box-sizing: border-box;
    /* Font scaling for desktop browsers (Chrome, Firefox, desktop Safari) */
    font-size: calc(16px * var(--font-size-scale, 1));
  }

  *,
  *:before,
  *:after {
    box-sizing: inherit;
    font-family: inherit;
  }

  body {
    font-family: ${fontFamilies.helveticaNowText};
    color: ${colors.liquorice};
    font-weight: 400;
    margin: 0;
    /* Allow text scaling via -webkit-text-size-adjust (set dynamically by fontSizeManager) */
    -webkit-text-size-adjust: 100%;
    ${/* The background color is also set in the HTML file directly to prevent a flash of content on mobile */ ''}
    background-color: ${colors.sugar};

    /* Prevent content from scrolling behind fixed navigation bar */
    /* This fixes first touch being interpreted as scroll instead of tap */
    overscroll-behavior-y: contain;
    -webkit-overflow-scrolling: touch;
  }

  a {
    color: ${colors.petrolBlue};
    text-decoration: none;
  }

  img {
    display: inline; /* override Chakra */
  }

  blockquote {
    margin: 0;
    padding: 0 ${spacing.deci};
    border-left: ${spacing.milli} solid ${colors.cloud};
  }

  input[type='text'],
  input[type='search'],
  input[type='email'],
  textarea {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
  }

  input,
  textarea {
    border: 1px solid ${colors.cloud};
    border-radius: 4px;
    padding: ${spacing.centi} ${spacing.deci};
    background-color: ${colors.sugar};
    outline: none;
    ${fontStyles.mouse}

    &:focus {
      border-color: ${colors.petrolBlue};
    }

    /* Combining this selector doesn't seem to work on Safari */
    &:focus-visible {
      border-color: ${colors.petrolBlue};
    }

    &::placeholder {
      color: ${colors.cloud};
    }
  }

  /* Critical fix: Prevent scrollable areas from accepting scroll gestures on navbar */
  [style*='overflow-y: scroll'],
  [style*='overflow: scroll'],
  [style*='overflow: auto'] {
    overscroll-behavior: contain;
  }

  /* Ensure fixed navigation elements block scroll-through touches */
  [style*='position: fixed'],
  nav,
  [role='navigation'] {
    pointer-events: auto;
    touch-action: manipulation;
  }
`;
